import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import {
    getEmployeesOptionsPaginated,
    getEmployeeContains,
} from '@/modules/Employee/services'
import {
    DatacentralHookForm,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field'

import { getWebsiteReferrers } from '../../../services'
import {
    AssignmentStatusTypes,
    ContactChanel,
    ContactStatusFilterTypes,
} from '../../../types/utils'
import {
    useLeadFilters,
    LeadFilters as LeadFiltersType,
} from '../LeadFiltersContext'
import { CONTACT_ASSIGNEE_DEPARTMENT_IDS } from '@/constants/departments'

const LeadFilters: React.FC = () => {
    const { t } = useTranslation()
    const { t: tMarketing } = useTranslation('marketing')
    const { filters, setFilters } = useLeadFilters()

    // Track if we're the source of filter changes to prevent sync loops
    const isInternalUpdate = useRef(false)
    const isFirstRender = useRef(true)

    const formControl = useForm<LeadFiltersType>({
        defaultValues: filters,
    })

    // Refs for stable access
    const setFiltersRef = useRef(setFilters)
    setFiltersRef.current = setFilters
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Sync form with EXTERNAL filters changes only (not our own updates)
    useEffect(() => {
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false
            return
        }
        formControl.reset(filters, { keepDefaultValues: true })
    }, [filters, formControl])

    // Debounced filter apply - reused by watch and onChangeAfterHookForm
    const applyFiltersDebounced = useCallback(
        (overrides?: Partial<LeadFiltersType>) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
            debounceTimerRef.current = setTimeout(() => {
                isInternalUpdate.current = true
                setFiltersRef.current({
                    ...formControl.getValues(),
                    q: '',
                    ...overrides,
                })
            }, 150)
        },
        [formControl]
    )

    // Auto-apply filters on form field change (debounced)
    useEffect(() => {
        const subscription = formControl.watch(() => {
            if (isFirstRender.current) {
                isFirstRender.current = false
                return
            }
            applyFiltersDebounced()
        })

        return () => {
            subscription.unsubscribe()
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [formControl, applyFiltersDebounced])

    const { data: websiteReferrersData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MARKETING, 'website-referrers'],
        queryFn: getWebsiteReferrers,
    })

    const websiteReferrerOptions =
        websiteReferrersData?.results?.map((item) => ({
            value: item.website_referrer,
            label: item.website_referrer,
        })) || []

    // Declarative filter form fields
    const formData: DatacentralHookFormDataItem<LeadFiltersType>[] = [
        // Row 1: Date, Status, Assignment, Channel
        {
            colSpan: 2,
            className: 'data-tour-filter-date',
            formItemProps: {
                label: tMarketing('created-at'),
                type: 'daterangepicker',
                name: [
                    'contact_source_created_at_gte',
                    'contact_source_created_at_lte',
                ],
                showPresets: true,
            },
        },
        {
            colSpan: 4,
            formItemProps: {
                label: tMarketing('contact_source_channel'),
                type: 'select',
                multiple: true,
                name: 'contact_source_channel',
                placeholder: tMarketing('contact_source_channel.placeholder'),
                options: ContactChanel(tMarketing),
            },
        },
        {
            colSpan: 3,
            helpModal: {
                modalName: 'ContactMarketingModals/LeadStatusLegendModal',
                title: t('marketing:lead_flow.title'),
            },
            formItemProps: {
                label: tMarketing('lead_flow.status'),
                type: 'select',
                name: 'contact_source_status',
                placeholder: tMarketing('lead-flow.status.placeholder'),
                options: ContactStatusFilterTypes(t),
                clearable: true,
                disabled: !!formControl.watch('assignment_status'),
                onAfterChangeValue: (_, { setValue }) => {
                    setValue('assignment_status', '' as any)
                },
            },
        },
        {
            colSpan: 3,
            helpModal: {
                modalName: 'ContactMarketingModals/AssignmentStatusLegendModal',
                title: t('marketing:assignment_status.legend_title'),
            },
            formItemProps: {
                label: tMarketing('assignment_status.legend_title'),
                type: 'select',
                name: 'assignment_status',
                placeholder: tMarketing('assignment_status.placeholder'),
                options: AssignmentStatusTypes(tMarketing),
                clearable: true,
                disabled: !!formControl.watch('contact_source_status'),
                onAfterChangeValue: (_, { setValue }) => {
                    setValue('contact_source_status', '' as any)
                },
            },
        },

        // Row 2: Product Group, Employee, Website
        {
            colSpan: 6,
            formItemProps: {
                type: 'select',
                multiple: true,
                name: 'contact_source_interested_services',
                label: t('contact-source-interested-services'),
                placeholder: t('contact-source-interested-services'),
                asyncOptions: getProductsGroupOptions,
            },
        },
        {
            colSpan: 3,
            formItemProps: {
                type: 'select',
                multiple: true,
                name: 'contact_source_assignee_ids',
                label: t('marketing:contact-source-assignee'),
                placeholder: t('marketing:contact-source-assignee'),
                asyncOptions: (query, page) =>
                    getEmployeesOptionsPaginated(
                        query,
                        CONTACT_ASSIGNEE_DEPARTMENT_IDS,
                        undefined,
                        page
                    ),
                getDefaultOptions: (ids) =>
                    getEmployeeContains({ employee_ids: ids }),
            },
        },
        {
            colSpan: 3,
            formItemProps: {
                type: 'select',
                name: 'website_referrer',
                placeholder: t('website-referrer'),
                options: websiteReferrerOptions,
                clearable: true,
            },
        },
    ]

    return (
        <div
            className="bg-white rounded-md border border-gray-200 p-4"
            data-tour="lead-filters"
        >
            <DatacentralHookForm
                formControl={formControl}
                data={formData}
                onSubmit={() => {}}
                subForm
                columns={12}
                className="gap-2"
            />
        </div>
    )
}

export default LeadFilters
